import { useEffect, useRef, useState } from "react";
import { Eye } from "lucide-react";
import { theme } from "../theme";
import { api, ApiError } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { AuditorFeedbackInline } from "./AuditorFeedbackInline";
import type { InlineFinding, InlineEvidenceRequest } from "./AuditorFeedbackInline";

interface Comment {
  id: string;
  body: string;
  createdAt: string;
  userDisplayName: string;
  userRole: string;
  visibleToClient: boolean;
}

const ROLE_LABELS: Record<string, string> = {
  client: "Client",
  consultant: "Consultant",
  auditor: "Auditor",
};

const ROLE_COLORS: Record<string, { bg: string; color: string }> = {
  client:     { bg: "#3b82f622", color: "#3b82f6" },
  consultant: { bg: "#a855f722", color: "#a855f7" },
  auditor:    { bg: "#f59e0b22", color: "#f59e0b" },
};

function formatTimestamp(isoStr: string): string {
  const d = new Date(isoStr);
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  const relative =
    secs < 60 ? "just now" :
    secs < 3600 ? `${Math.floor(secs / 60)}m ago` :
    secs < 86400 ? `${Math.floor(secs / 3600)}h ago` :
    secs < 604800 ? `${Math.floor(secs / 86400)}d ago` : null;
  const absolute = d.toLocaleString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
  return relative ? `${relative} · ${absolute}` : absolute;
}

/**
 * Append-only notes thread for one control / requirement. Every saved note is
 * listed below the input with its author and timestamp and the input clears;
 * the same component backs every framework, the legal obligations page,
 * Cyber Essentials and hardening so notes behave identically everywhere.
 *
 * `legacyNote` is the single free-text field a page stored before it had a
 * thread (SoaEntry.justification, ...). It is shown as
 * an "Earlier note" until a saved note carries the same text, so nothing
 * already written is hidden. `onPost` lets the page mirror the newest note
 * back into that field for the AI insights and reports that still read it.
 */
export function ControlComments({
  controlId,
  open,
  label,
  orgId,
  onPost,
  consultantMode,
  legacyNote,
  placeholder,
  submitLabel,
  emptyText,
  canWrite,
}: {
  controlId: string;
  open: boolean;
  label?: string;
  orgId?: string;
  onPost?: (body: string) => void;
  consultantMode?: boolean;
  legacyNote?: string | null;
  placeholder?: string;
  submitLabel?: string;
  emptyText?: string;
  /** Whether the viewer may add notes. Defaults to controls:write, what every note write needs on the server. */
  canWrite?: boolean;
}) {
  const { user } = useCurrentUser();
  const mayWrite = canWrite ?? (!!user?.permissions?.includes("controls:write"));
  const [comments, setComments] = useState<Comment[] | null>(null);
  const [viewerRole, setViewerRole] = useState<string | null>(null);
  const [findings, setFindings] = useState<InlineFinding[]>([]);
  const [evidenceRequests, setEvidenceRequests] = useState<InlineEvidenceRequest[]>([]);
  const [draft, setDraft] = useState("");
  const [postVisible, setPostVisible] = useState(false);
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [editError, setEditError] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);

  const scope = orgId ? `?organizationId=${encodeURIComponent(orgId)}` : "";
  // A consultant gets the visibility controls whether the page was written for
  // the consultant portal or is a client page opened inside it.
  const isConsultant = consultantMode || viewerRole === "consultant";

  useEffect(() => {
    if (!open) return;
    api.get<{ comments: Comment[]; viewerRole?: string; findings?: InlineFinding[]; evidenceRequests?: InlineEvidenceRequest[] }>(`/controls/${controlId}/comments${scope}`)
      .then((d) => {
        setComments(d.comments);
        setViewerRole(d.viewerRole ?? null);
        setFindings(d.findings ?? []);
        setEvidenceRequests(d.evidenceRequests ?? []);
      })
      .catch(() => setComments([]));
  }, [open, controlId, scope]);

  async function submit() {
    const body = draft.trim();
    if (!body || posting) return;
    setPosting(true);
    setPostError("");
    try {
      const data = await api.post<{ comment: Comment }>(
        `/controls/${controlId}/comments${scope}`,
        { body, visibleToClient: isConsultant ? postVisible : undefined },
      );
      setComments((prev) => [...(prev ?? []), data.comment]);
      setDraft("");
      setPostVisible(false);
      onPost?.(body);
    } catch (e) {
      // Leave the draft in place so nothing typed is lost.
      setPostError(
        e instanceof ApiError && e.status === 403
          ? "Your role does not permit adding notes here."
          : e instanceof ApiError ? e.message : "Could not save the note. Please try again.",
      );
    } finally {
      setPosting(false);
    }
  }

  async function handleEditSave(commentId: string) {
    const body = editDraft.trim();
    if (!body) return;
    setEditError("");
    try {
      const data = await api.patch<{ comment: Comment }>(
        `/controls/${controlId}/comments/${commentId}${scope}`,
        { body },
      );
      const before = comments?.find((c) => c.id === commentId)?.body ?? "";
      setComments((prev) => prev?.map((c) => c.id === commentId ? data.comment : c) ?? null);
      setEditingId(null);
      // The control's summary note (what the check, the consultant and exports read) follows the
      // thread: if it mirrored the note just edited, it now carries the edited text.
      if (mirrorsLegacy(before)) onPost?.(data.comment.body);
    } catch (e) {
      setEditError(e instanceof ApiError ? e.message : "Failed to save — please try again.");
    }
  }

  /** Whether the summary note (legacyNote) is a copy of this note's text. */
  function mirrorsLegacy(body: string): boolean {
    const legacy = legacyNote?.trim() ?? "";
    return !!legacy && body.trim().startsWith(legacy);
  }

  async function handleDelete(commentId: string) {
    setDeleteError("");
    try {
      await api.del(`/controls/${controlId}/comments/${commentId}${scope}`);
      const deleted = comments?.find((c) => c.id === commentId)?.body ?? "";
      const remaining = (comments ?? []).filter((c) => c.id !== commentId);
      setComments(remaining);
      setConfirmDeleteId(null);
      // A withdrawn note never lives on as the summary note: it becomes the newest remaining one, or nothing.
      if (mirrorsLegacy(deleted)) onPost?.(remaining.length ? remaining[remaining.length - 1].body : "");
    } catch (e) {
      setDeleteError(e instanceof ApiError ? e.message : "Failed to delete — please try again.");
    }
  }

  async function toggleVisibility(commentId: string, current: boolean) {
    try {
      const data = await api.patch<{ comment: Comment }>(
        `/controls/${controlId}/comments/${commentId}${scope}`,
        { visibleToClient: !current },
      );
      setComments((prev) => prev?.map((c) => c.id === commentId ? data.comment : c) ?? null);
    } catch {
      // visibility toggle is best-effort; no disruptive error shown
    }
  }

  if (!open) return null;

  const smallBtn: React.CSSProperties = {
    background: "transparent", border: "none", cursor: "pointer",
    fontSize: 11, padding: "2px 6px", borderRadius: 4,
  };

  // The pre-thread free-text note, until a saved note carries the same text
  // (a mirrored copy may be truncated to the legacy column's limit).
  const trimmedLegacy = legacyNote?.trim() ?? "";
  const showLegacy = !!trimmedLegacy && comments !== null && !comments.some((c) => c.body.trim().startsWith(trimmedLegacy));
  const isEmpty = comments !== null && comments.length === 0 && !showLegacy;

  return (
    <div style={{ borderTop: `1px solid ${theme.color.border}`, paddingTop: 12, marginTop: 4 }}>
      <AuditorFeedbackInline findings={findings} evidenceRequests={evidenceRequests} orgId={orgId} />
      <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.heading, marginBottom: 10 }}>{label ?? "Notes"}</div>

      {comments === null ? (
        <div style={{ fontSize: 12, color: theme.color.faint }}>Loading…</div>
      ) : isEmpty ? (
        <div style={{ fontSize: 12, color: theme.color.faint, marginBottom: 10 }}>{emptyText ?? "No notes yet."}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
          {showLegacy && (
            <div style={{ background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "8px 12px" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.faint, marginBottom: 4 }}>Earlier note</div>
              <div style={{ fontSize: 12, color: theme.color.text, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{trimmedLegacy}</div>
            </div>
          )}
          {comments.map((c) => {
            const rc = ROLE_COLORS[c.userRole] ?? ROLE_COLORS.client;
            const isEditing = editingId === c.id;
            const isConfirmingDelete = confirmDeleteId === c.id;
            return (
              <div key={c.id} style={{ background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "8px 12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: rc.color, background: rc.bg, padding: "1px 7px", borderRadius: 3 }}>
                    {ROLE_LABELS[c.userRole] ?? c.userRole}
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading }}>{c.userDisplayName}</span>
                  {isConsultant && c.userRole !== "client" && (
                    <button
                      onClick={() => toggleVisibility(c.id, c.visibleToClient)}
                      title={c.visibleToClient ? "Click to hide from client" : "Click to make visible to client"}
                      style={{
                        ...smallBtn,
                        fontSize: 10, padding: "1px 8px", borderRadius: 3,
                        border: `1px solid ${c.visibleToClient ? theme.color.success : theme.color.borderStrong}`,
                        color: c.visibleToClient ? theme.color.successText : theme.color.faint,
                        background: c.visibleToClient ? `${theme.color.success}18` : "transparent",
                      }}
                    >
                      {c.visibleToClient ? <><Eye size={11} style={{ verticalAlign: "-1px", marginRight: 4 }} />Visible to client</> : "Hidden from client"}
                    </button>
                  )}
                  <span style={{ fontSize: 11, color: theme.color.faint, marginLeft: "auto" }}>{formatTimestamp(c.createdAt)}</span>
                  {mayWrite && !isEditing && !isConfirmingDelete && (
                    <>
                      <button onClick={() => { setEditingId(c.id); setEditDraft(c.body); }} style={{ ...smallBtn, color: theme.color.muted }}>Edit</button>
                      <button onClick={() => setConfirmDeleteId(c.id)} style={{ ...smallBtn, color: theme.color.danger }}>Delete</button>
                    </>
                  )}
                  {isConfirmingDelete && (
                    <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, flexWrap: "wrap" }}>
                      <span style={{ color: theme.color.danger }}>Delete?</span>
                      <button onClick={() => handleDelete(c.id)} style={{ ...smallBtn, color: theme.color.danger, fontWeight: 700 }}>Yes</button>
                      <button onClick={() => { setConfirmDeleteId(null); setDeleteError(""); }} style={{ ...smallBtn, color: theme.color.muted }}>No</button>
                      {deleteError && <span style={{ color: theme.color.dangerText }}>{deleteError}</span>}
                    </span>
                  )}
                </div>
                {isEditing ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <div style={{ display: "flex", gap: 6, alignItems: "flex-end" }}>
                      <textarea
                        value={editDraft}
                        onChange={(e) => setEditDraft(e.target.value)}
                        rows={2}
                        style={{
                          flex: 1, resize: "vertical", background: "var(--c-inputBg)", border: `1px solid ${theme.color.borderStrong}`,
                          borderRadius: 3, padding: "6px 8px", fontSize: 12, color: theme.color.text,
                          fontFamily: theme.font, lineHeight: 1.5,
                        }}
                      />
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <button onClick={() => handleEditSave(c.id)} disabled={!editDraft.trim()} style={{ padding: "4px 10px", borderRadius: 3, fontSize: 11, fontWeight: 600, background: theme.color.accent, color: "#fff", border: "none", cursor: "pointer" }}>Save</button>
                        <button onClick={() => { setEditingId(null); setEditError(""); }} style={{ padding: "4px 10px", borderRadius: 3, fontSize: 11, background: "transparent", border: `1px solid ${theme.color.borderStrong}`, color: theme.color.muted, cursor: "pointer" }}>Cancel</button>
                      </div>
                    </div>
                    {editError && <div style={{ fontSize: 11, color: theme.color.dangerText }}>{editError}</div>}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: theme.color.text, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{c.body}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {mayWrite && isConsultant && (
        <label style={{
          display: "flex", alignItems: "center", gap: 6, fontSize: 11,
          color: postVisible ? theme.color.successText : theme.color.muted,
          cursor: "pointer", marginBottom: 6, userSelect: "none",
        }}>
          <input
            type="checkbox"
            checked={postVisible}
            onChange={(e) => setPostVisible(e.target.checked)}
            style={{ accentColor: theme.color.success }}
          />
          Visible to client
        </label>
      )}

      {mayWrite && <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
        <textarea
          ref={textRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); } }}
          placeholder={placeholder ?? "Add a note… (Ctrl+Enter to save)"}
          rows={2}
          style={{
            flex: 1, resize: "vertical", background: "var(--c-inputBg)", border: `1px solid ${theme.color.borderStrong}`,
            borderRadius: 3, padding: "8px 10px", fontSize: 12, color: theme.color.text,
            fontFamily: theme.font, lineHeight: 1.5,
          }}
        />
        <button
          onClick={submit}
          disabled={!draft.trim() || posting}
          style={{
            padding: "8px 16px", borderRadius: 3, fontSize: 12, fontWeight: 600,
            background: theme.color.accent, color: "#fff", border: "none",
            cursor: draft.trim() && !posting ? "pointer" : "not-allowed",
            opacity: !draft.trim() || posting ? 0.5 : 1, whiteSpace: "nowrap",
          }}
        >
          {posting ? "Saving…" : (submitLabel ?? "Save note")}
        </button>
      </div>}
      {postError && <div style={{ fontSize: 11, color: theme.color.dangerText, marginTop: 6 }}>{postError}</div>}
    </div>
  );
}
